import { TAB_TITLE } from 'containers/Product/constants'
import { k8V2ContentfulMappings } from 'utils/productMaps'
import { toTitleCase } from 'utils/stringHelpers'

import {
  getClassTier,
  isClassFinished,
} from 'components/ProductLessons/classListHelpers'

import {
  DIGITAL_WELL_BEING_PRODUCT_NAME,
  LESSONS_PROGRAM_CONFIG,
} from './constants'

const normalizeTitle = title => title?.toLowerCase()

const getDisplayTitleMapping = internalTitle => {
  const normalizedInternalTitle = normalizeTitle(internalTitle)

  return k8V2ContentfulMappings.find(mapping =>
    normalizedInternalTitle?.includes(normalizeTitle(mapping?.displayTitle)),
  )
}

export const addDisplayTitlesToCourseTree = courseTree => {
  if (!courseTree || !courseTree.internalTitle) {
    return courseTree
  }

  const matchingMapping = getDisplayTitleMapping(courseTree.internalTitle)

  if (!matchingMapping) {
    return courseTree
  }

  return {
    ...courseTree,
    displayTitle: matchingMapping.displayTitle,
  }
}

/**
 * Processes a collection of course trees by converting object to array and adding display titles
 * @param {Object} courseTreeCollection - Object containing course trees as values
 * @returns {Array} Array of processed course trees with display titles added where matches are found
 */
export const processCourseTreeCollection = courseTreeCollection => {
  if (!courseTreeCollection || typeof courseTreeCollection !== 'object') {
    return []
  }
  return Object.values(courseTreeCollection).map(courseTree =>
    addDisplayTitlesToCourseTree(courseTree),
  )
}

const normalizeSiteEducatorList = siteEducator => {
  if (Array.isArray(siteEducator)) return siteEducator
  if (siteEducator) return [siteEducator]
  return []
}

const getSiteEducatorForProgram = (instructorData, instructorKey) => {
  if (!instructorData) return null

  // withSiteContext passes program-scoped instructor data ({ siteEducator })
  if (Object.prototype.hasOwnProperty.call(instructorData, 'siteEducator')) {
    return instructorData.siteEducator
  }

  return instructorData[instructorKey]?.siteEducator
}

export const selectLessonsCurriculumClasses = ({
  instructorData,
  userContext,
  programKey,
}) => {
  const config = LESSONS_PROGRAM_CONFIG[programKey]
  if (!config || !instructorData) return []

  const siteEducator = getSiteEducatorForProgram(
    instructorData,
    config.instructorKey,
  )
  const rawClasses = normalizeSiteEducatorList(siteEducator)

  const activeSiteId = userContext?.[config.activeSiteKey]
  const siteFilteredClasses =
    activeSiteId == null
      ? rawClasses
      : rawClasses.filter(
          course => String(course?.site_id) === String(activeSiteId),
        )

  return siteFilteredClasses.filter(
    course => !(getClassTier(course) === 2 && isClassFinished(course)),
  )
}

export const getLessonPageViewModel = ({
  match,
  productName,
  productTabTitle,
}) => {
  const baseUrl = match?.url ?? ''
  const basePath = match?.path ?? baseUrl
  const productTitle = toTitleCase(
    `${productName?.replace(/-/g, ' ') ?? ''} ${
      productName === DIGITAL_WELL_BEING_PRODUCT_NAME ? 'Unit' : 'Lessons'
    }`.trim(),
  )
  const withVerb = `${basePath}/:verb(preview|teach)`
  const tabTitle = [productTabTitle, TAB_TITLE].filter(Boolean).join(' | ')

  return {
    baseUrl,
    productTitle,
    tabTitle,
    withVerb,
  }
}
